import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useTheme } from './ThemeProvider';
import { cn } from '../lib/utils';
import { Button } from './Button';
import { Badge } from './Badge';
import { AppLauncher } from './AppLauncher';
import { CheckInOutButton } from './attendance/CheckInOut';
import { InstallPWA } from './InstallPWA';
import { Tooltip, TooltipProvider } from './Tooltip';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from './DropdownMenu';
import {
  Search, Bell, Sun, Moon, Command, Menu, Plus, CheckCircle2,
  UserPlus, FileText, FolderPlus, ChevronRight, Settings, MessageSquare,
} from 'lucide-react';

const LABELS: Record<string, string> = {
  people: 'People', work: 'Work', money: 'Money', assets: 'Assets',
  support: 'Support', growth: 'Growth', admin: 'Admin', dashboard: 'Dashboard',
  employees: 'Employees', attendance: 'Attendance', leave: 'Leave',
  payroll: 'Payroll', recruitment: 'Recruitment', performance: 'Performance',
  projects: 'Projects', tasks: 'Tasks', invoices: 'Invoices', expenses: 'Expenses',
  customers: 'Customers', sales: 'Sales', tickets: 'Tickets', profile: 'Profile',
  settings: 'Settings', new: 'New', accounting: 'Accounting', billing: 'Billing',
  organization: 'Organization', users: 'Users', roles: 'Roles', security: 'Security',
  requests: 'Requests & Approvals', approval: 'Requests & Approvals', requisitions: 'Requests & Approvals',
};

import { useAdminNotificationStore, notificationLink } from '../store/adminNotificationStore';
import { useRbacStore } from '../store/rbacStore';
import { useAuthStore } from '../store/authStore';
import { useTicketStore } from '../store/ticketStore';
import { useAssetStore } from '../store/assetStore';
import { Avatar, AvatarImage, AvatarFallback } from './Avatar';
import { getUserAvatarUrl } from '../utils/avatar';
import { User as UserIcon, LogOut, UserCheck } from 'lucide-react';
import { useEffect } from 'react';

const initials = (name?: string) =>
  name
    ? name
        .split(' ')
        .map((n) => n[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : 'U';

const prettify = (seg: string) =>
  LABELS[seg] ??
  seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export function TopNav({ onMenuClick }: { onMenuClick?: () => void }) {
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { items: adminItems, unread: adminUnread, load: loadAdmin, markRead, markAllRead } = useAdminNotificationStore();
  const roles = useRbacStore((s) => s.roles);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  // The sidebar (with its own profile menu) is always visible now, so the
  // portal-only top-nav profile dropdown is no longer shown.
  const showTopNavProfile = false;

  // Refresh server-side admin alerts when the user's roles are known.
  useEffect(() => {
    loadAdmin();
  }, [loadAdmin, roles.length]);

  const currentTicket = useTicketStore((s) => s.currentTicket);
  const currentAsset = useAssetStore((s) => s.currentAsset);
  const isTicketsPage = location.pathname === '/support/tickets' || location.pathname.startsWith('/support/tickets');

  const segments = location.pathname.split('/').filter(Boolean);
  const crumbs: Array<{ label: string; href: string; onClick?: () => void }> = segments.map((seg, i) => ({
    label: prettify(seg),
    href: '/' + segments.slice(0, i + 1).join('/'),
  }));

  const unreadCount = adminUnread;

  return (
    <TooltipProvider delayDuration={300}>
      <header className="sticky top-0 z-40 flex h-14 w-full items-center gap-3 border-b border-border bg-card px-4 md:px-6">
        {/* Mobile menu */}
        <Button
          variant="ghost"
          size="icon-sm"
          className="md:hidden"
          onClick={onMenuClick}
          aria-label="Open menu"
        >
          <Menu />
        </Button>

        {/* App launcher — switch between apps */}
        <Tooltip label="Switch app" side="bottom">
          <div>
            <AppLauncher />
          </div>
        </Tooltip>

        <span className="hidden h-6 w-px bg-border md:block" />

        {/* Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="hidden min-w-0 items-center md:flex">
          <ol className="flex items-center gap-1 text-sm">
            <li>
              <Link
                to="/dashboard"
                onClick={() => useTicketStore.getState().setCurrentTicket(null)}
                className="rounded px-1 py-0.5 text-text-tertiary transition-colors hover:text-foreground"
              >
                Home
              </Link>
            </li>
            {crumbs.map((c, i) => {
              const last = i === crumbs.length - 1;
              return (
                <li key={c.href + i} className="flex min-w-0 items-center gap-1">
                  <ChevronRight className="size-3.5 shrink-0 text-text-subtle" />
                  {last ? (
                    <span className="truncate px-1 py-0.5 font-medium text-foreground">
                      {c.label}
                    </span>
                  ) : (
                    <Link
                      to={c.href}
                      onClick={c.onClick}
                      className="truncate rounded px-1 py-0.5 text-text-tertiary transition-colors hover:text-foreground"
                    >
                      {c.label}
                    </Link>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>

        <div className="flex flex-1 items-center justify-end gap-1.5">
          {/* Search */}
              <button
                className="group mr-1 hidden h-9 items-center gap-2 rounded-md border border-border bg-surface-2 px-3 text-sm text-muted-foreground transition-colors hover:border-border-strong hover:text-foreground sm:flex sm:w-48 lg:w-64"
                onClick={() => { /* command palette hook */ }}
              >
                <Search className="size-4" />
                <span className="flex-1 text-left">Search…</span>
                <kbd className="inline-flex h-5 items-center gap-0.5 rounded-sm border border-border bg-card px-1.5 font-mono text-xs font-medium text-muted-foreground">
                  <Command className="size-3" />K
                </kbd>
              </button>

              <Button variant="ghost" size="icon-sm" className="size-8 text-muted-foreground hover:text-foreground sm:hidden" aria-label="Search">
                <Search />
              </Button>

              {/* Attendance: check in / check out from any page */}
              <CheckInOutButton className="mr-1" />

              {/* Quick create */}
              <DropdownMenu>
                <Tooltip label="Create new" side="bottom">
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon-sm" className="size-8 text-muted-foreground hover:text-foreground" aria-label="Create new">
                      <Plus />
                    </Button>
                  </DropdownMenuTrigger>
                </Tooltip>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuLabel>Quick create</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => navigate('/people/employees/new')}>
                    <UserPlus className="size-4" /> New employee
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/work/projects/new')}>
                    <FolderPlus className="size-4" /> New project
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/money/invoices/new')}>
                    <FileText className="size-4" /> New invoice
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

          {/* Notifications */}
          <DropdownMenu>
            <Tooltip label="Notifications" side="bottom">
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm" className="size-8 text-muted-foreground hover:text-foreground relative" aria-label="Notifications">
                  <Bell />
                  {unreadCount > 0 && (
                    <span className="absolute right-1 top-1 size-2 rounded-full bg-destructive ring-2 ring-background animate-pulse" />
                  )}
                </Button>
              </DropdownMenuTrigger>
            </Tooltip>
            <DropdownMenuContent align="end" className="w-80 p-0">
              <div className="flex items-center justify-between px-3.5 py-3 border-b border-border">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">Notifications</span>
                  {unreadCount > 0 && <Badge variant="primary">{unreadCount} new</Badge>}
                </div>
                {unreadCount > 0 && (
                  <button
                    onClick={() => markAllRead()}
                    className="text-xs text-primary hover:underline font-medium"
                  >
                    Mark all read
                  </button>
                )}
              </div>
              <div className="max-h-80 overflow-y-auto py-1 divide-y divide-border">
                {adminItems.length === 0 ? (
                  <div className="p-6 text-center text-xs text-muted-foreground">
                    No notifications yet
                  </div>
                ) : (
                  adminItems.slice(0, 8).map((n) => {
                    const isWarning = n.type === 'warning';
                    return (
                      <div
                        key={n.id}
                        onClick={() => {
                          if (!n.is_read) markRead(n.id);
                          const link = notificationLink(n);
                          if (link) navigate(link);
                        }}
                        className={cn(
                          'flex cursor-pointer items-start gap-3 px-3.5 py-2.5 transition-colors hover:bg-secondary/40',
                          !n.is_read && (isWarning ? 'bg-warning/10' : 'bg-primary/5')
                        )}
                      >
                        <span
                          className={cn(
                            'mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md [&_svg]:size-4',
                            isWarning ? 'bg-warning/15 text-warning' : 'bg-secondary text-primary'
                          )}
                        >
                          {isWarning ? <UserPlus /> : <Bell />}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className={cn("text-xs leading-snug text-foreground", !n.is_read ? "font-semibold" : "font-medium")}>
                            {n.title}
                          </p>
                          <p className="truncate text-xs text-muted-foreground mt-0.5">{n.message}</p>
                        </div>
                        <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                          {new Date(n.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="p-1 border-t border-border">
                <button
                  onClick={() => navigate('/notifications')}
                  className="w-full px-3 py-2 text-center text-xs font-semibold text-primary hover:bg-secondary/40 rounded-md transition-colors"
                >
                  View all notifications ({adminItems.length})
                </button>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Install PWA Prompt */}
          <InstallPWA />

          {/* Theme toggle */}
          <Tooltip label={theme === 'dark' ? 'Light mode' : 'Dark mode'} side="bottom">
            <Button variant="ghost" size="icon-sm" className="size-8 text-muted-foreground hover:text-foreground" onClick={toggleTheme} aria-label="Toggle theme">
              {theme === 'dark' ? <Sun /> : <Moon />}
            </Button>
          </Tooltip>

          {/* Settings */}
          <Tooltip label="Settings" side="bottom">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => navigate('/settings')}
              aria-label="Settings"
              className="size-8 text-muted-foreground hover:text-foreground hidden sm:inline-flex"
            >
              <Settings />
            </Button>
          </Tooltip>

          {/* User Profile avatar dropdown (shown in portal view when sidebar is hidden) */}
          {showTopNavProfile && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 rounded-full p-0.5 transition-opacity hover:opacity-80 focus:outline-hidden"
                  aria-label="User account"
                >
                  <Avatar className="size-8 shrink-0">
                    <AvatarImage src={user?.avatar || getUserAvatarUrl(user?.name, user?.email)} alt={user?.name || 'User'} />
                    <AvatarFallback className="text-xs font-medium bg-primary text-white">
                      {initials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-xs font-medium leading-none">{user?.name || 'User'}</p>
                    <p className="text-[11px] leading-none text-muted-foreground">
                      {user?.email || ''}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/settings/profile')}>
                  <UserIcon className="mr-2 size-4" />
                  <span>Profile</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/admin/users?tab=access')}>
                  <Settings className="mr-2 size-4" />
                  <span>User &amp; Access Settings</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={logout}
                  className="text-destructive focus:text-destructive cursor-pointer"
                >
                  <LogOut className="mr-2 size-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </header>
    </TooltipProvider>
  );
}
